import { Attachment, ChangeEvent, ClickEvent, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, DropdownSelectionEvent, Image, Label, LookupModelSearchEvent, Panel, Textbox, ValidationResult } from "@mcleod/components";
import { CompanySettings, EmailUtil, McLeodClassicIntegration, Navigation, StringUtil } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { AutogenLayoutWebControl } from "./autogen/AutogenLayoutWebControl";
import { LMECompanySettings } from "./settings/LMECompanySettings";

const DOCUMENT_TYPE_RATE_CONFIRMATION = "Z";
const DOCUMENT_TYPE_EMAIL = "X";
const DOCUMENT_TYPE_DELIV_REC = "R";

export class WebControl extends AutogenLayoutWebControl {

    async onLoad() {
        this.setRequiredStatusForRatingScreenTrigger();

        this.textboxTargetPayCalc.addAfterDropdownSelectionListener((event: DropdownSelectionEvent) => {
            if (event.hasSelection()) {
                this.showHideMPactApiKeyTB(event.selection.value);
            }
        });

        this.textboxNotificationEmail.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
            return this.emailDomainValidator(this.textboxNotificationEmail, mode);
        };

        this.tabMandatoryCarrierRatings.visible = LMECompanySettings.get().carrier_scorecard_licensed;

        this.attachmentOfferOrderLogo.setAttachmentWithUI = async (file: File) => {
            this.saveImageCorrectly(file, this.attachmentOfferOrderLogo);
        };

        this.attachmentWaterfallLogo.setAttachmentWithUI = async (file: File) => {
            this.saveImageCorrectly(file, this.attachmentWaterfallLogo);
        };

        const levels = [5, 4, 3, 2, 1];
        levels.forEach((level, idx) => {
            const textbox = this[`textboxGreaterThan${level}`];
            if (textbox) {
                textbox.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
                    return this.scorecardMinValidator(level);
                };
            }
            const maxTextbox = this[`textboxEqualToLess${level}`];
            if (maxTextbox) {
                maxTextbox.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
                    return this.scorecardMaxValidator(level);
                };
            }
        });
    }

    private emailDomainValidator(textbox: Textbox, mode: DataSourceMode) {
        if (mode === DataSourceMode.SEARCH) {
            return null;
        }

        // Allow empty strings
        if (!textbox.text || textbox.text.trim() === '') {
            return null;
        }

        // Reject strings containing only commas and whitespace
        if (/^[\s,]*$/.test(textbox.text)) {
            return { component: textbox, isValid: false, validationMessage: "At least one valid email domain is required." };
        }

        const result: ValidationResult = { isValid: true, component: textbox, validationMessage: null };
        // Split on comma with optional whitespace and filter out empty domains
        const domains = textbox.text.split(/,\s*/).filter(domain => domain.trim() !== '');

        for (const domain of domains) {
            if (!EmailUtil.isValidEmail(domain.trim())) {
                return { component: textbox, isValid: false, validationMessage: "Invalid domain format. Email domains must be in the following format [abc.com,xyz.com]" }
            }
        }
        return null;
    }

    /** This is an event handler for the afterExecution event of sourceDispatchControl.  */
    async sourceDispatchControlAfterExecution(event: DataSourceExecutionEvent) {

        this.showHideMPactApiKeyTB(this.mainDataSource.activeRow.get("target_pay_calc"));
        if (event.getAction() === DataSourceAction.UPDATE) {
            Navigation.reloadCurrentPage(true);
        }

        this.setScoreCardMaximums();
    }

    /** This is an event handler for the beforeExecution event of sourceDispatchControl.  */
    sourceDispatchControlBeforeExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.UPDATE) {
            if (this.activeRow.getChangedData()?.mpact_smart_api_key) {
                this.activeRow.set("mpact_key_last_entered", new Date());
            }
            if (this.activeRow.getChangedData()?.rate_confirmation_template) {
                this.activeRow.set("conf_fax_template", this.activeRow.get("rate_confirmation_template", ""));
            }
            if (this.activeRow.getChangedData()?.ins_expiration_template) {
                this.activeRow.set("ins_exp_templateid", this.activeRow.get("ins_expiration_template", ""));
            }
        }
    }

    private scorecardMinValidator(level: number): ValidationResult {
        // Validate only this min and its relationship to its max and the max below
        this.setScoreCardMaximums();
        const minTextbox = this[`textboxGreaterThan${level}`];
        const maxTextbox = this[`textboxEqualToLess${level}`];
        const minVal = parseInt(minTextbox.text, 10);
        const maxVal = parseInt(maxTextbox.text, 10);
        if (isNaN(minVal) || minVal < 0 || minVal > 100) {
            return {
                isValid: false,
                component: minTextbox,
                validationMessage: "Value must be a number between 0 and 100."
            };
        }
        if (isNaN(maxVal) || maxVal < 0 || maxVal > 100) {
            return {
                isValid: false,
                component: maxTextbox,
                validationMessage: "Value must be a number between 0 and 100."
            };
        }
        if (minVal >= maxVal) {
            return {
                isValid: false,
                component: minTextbox,
                validationMessage: `Minimum for ${level} star must be less than its maximum.`
            };
        }
        // For 4,3,2,1 star: min for this star must equal max for the star below
        if (level !== 1) {
            const maxBelow = this[`textboxEqualToLess${level - 1}`];
            if (maxBelow && minTextbox.text !== maxBelow.text) {
                return {
                    isValid: false,
                    component: minTextbox,
                    validationMessage: `No gaps or overlaps allowed: minimum for ${level} star must equal maximum for ${level - 1} star.`
                };
            }
        }
        return { isValid: true, component: null, validationMessage: null };
    }

    private scorecardMaxValidator(level: number): ValidationResult {
        // Validate only this max and its relationship to its min and the min above
        const minTextbox = this[`textboxGreaterThan${level}`];
        const maxTextbox = this[`textboxEqualToLess${level}`];
        const minVal = parseInt(minTextbox.text, 10);
        const maxVal = parseInt(maxTextbox.text, 10);
        if (isNaN(maxVal) || maxVal < 0 || maxVal > 100) {
            return {
                isValid: false,
                component: maxTextbox,
                validationMessage: "Value must be a number between 0 and 100."
            };
        }
        if (isNaN(minVal) || minVal < 0 || minVal > 100) {
            return {
                isValid: false,
                component: minTextbox,
                validationMessage: "Value must be a number between 0 and 100."
            };
        }
        if (minVal >= maxVal) {
            return {
                isValid: false,
                component: maxTextbox,
                validationMessage: `Maximum for ${level} star must be greater than its minimum.`
            };
        }
        // For 5,4,3,2 star: max for this star must equal min for the star above
        if (level !== 5) {
            const minAbove = this[`textboxGreaterThan${level + 1}`];
            if (minAbove && maxTextbox.text !== minAbove.text) {
                return {
                    isValid: false,
                    component: maxTextbox,
                    validationMessage: `No gaps or overlaps allowed: maximum for ${level} star must equal minimum for ${level + 1} star.`
                };
            }
        }
        return { isValid: true, component: null, validationMessage: null };
    }

    setScoreCardMaximums() {
        const levels = [5, 4, 3, 2, 1];
        const minTextboxes = levels.map(i => this[`textboxGreaterThan${i}`]);
        const maxTextboxes = levels.map(i => this[`textboxEqualToLess${i}`]);

        // 5-star max is always 100
        maxTextboxes[0].text = "100";

        // Each lower star's max = min of the star above
        for (let i = 0; i < levels.length - 1; i++) {
            maxTextboxes[i + 1].text = minTextboxes[i].text;
        }
    }

    textboxDeliveryReceiptFormatBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.document_type = DOCUMENT_TYPE_DELIV_REC;
        event.filter.template_number = ">0";
    }

    showHideMPactApiKeyTB(value: string) {
        this.textboxMPactAPI.visible = value === "M";
        this.textboxMpactHostURL.visible = value === "M";
        this.textboxMpactKeyLastEntered.visible = value === "M";
        const companyScac = CompanySettings.get().scac;
        if (companyScac == null) {
            this.imageMPactSmartTargetWarning.visible = value === "M";
            const tooltipPanel = new Panel();
            tooltipPanel.add(new Label({ text: "No SCAC is set in company settings. A SCAC is required to use the MPact Smart Target API.", rowBreak: false }));
            const classicButton = new Image({ name: ImageName.classicMode, marginLeft: 10, tooltip: "Open Company Classic screen", onClick: () => McLeodClassicIntegration.openClassicScreen("com.tms.client.lib.EntryCompany", CompanySettings.get().id) });
            tooltipPanel.add(classicButton);
            this.imageMPactSmartTargetWarning.tooltipDynamicContent = tooltipPanel;
        }
    }

    textboxDoBeforeCarrierRateConfirmationFormat(event: LookupModelSearchEvent) {
        event.filter.document_type = DOCUMENT_TYPE_RATE_CONFIRMATION;
        event.filter.template_number = ">0";
    }

    textboxDoBeforeExpirationNotificationFormat(event: LookupModelSearchEvent) {
        event.filter.document_type = DOCUMENT_TYPE_EMAIL;
        event.filter.template_number = ">0";
    }

    textboxDoBeforeCallinScriptTemplate(event) {
        event.filter.is_active = "Y";
    }

    setRequiredStatusForRatingScreenTrigger() {
        this.textboxRatingTriggerCode.required = this.isMandatoryRatingsChecked();
        this.textboxRatingTriggerCode.enabled = this.isMandatoryRatingsChecked();
    }

    private isMandatoryRatingsChecked(): boolean {
        return this.checkboxMandatoryRatings?.getDataValue() === 'Y';
    }

    private saveImageCorrectly(file: File, attachment: Attachment) {
        const maxSize = attachment.maxSize;
        const fileName = file.name.toLowerCase();
        if (!fileName.endsWith(".jpg") && !fileName.endsWith(".png") && !fileName.endsWith(".gif") && !fileName.endsWith(".jpeg")) {
            return attachment.showAdhocTooltip("The file must be an image.", { shaking: true, timeout: 5000 });
        } else if (maxSize != null && file.size > maxSize)
            return attachment.showAdhocTooltip("The file is too big to attach. The file must be smaller than " + maxSize / (1024 * 1024) + "MB.", { shaking: true, timeout: 5000 });
        else {
            attachment.setFile(file);
        }
    }

    switchDisplayBtmOnChange(event: ChangeEvent) {
        this.toggleBuyToMarketFields(event.newValue);
    }

    private toggleBuyToMarketFields(value: boolean) {
        this.tabBuyToMarket.forEveryChildComponent(comp => comp.enabled = comp.id == 'switchDisplayBtm' || value);
    }

    buttonOpenClassicOnClick(event: ClickEvent) {
        const rateConfFormat = this.activeRow?.get("rate_confirmation_template") as string;
        McLeodClassicIntegration.openClassicScreen("com.tms.client.loadmaster.drs.EntryReportTemplate",
            "<ext> search report_template.template_number='" + rateConfFormat + "' and report_template.document_type='Z'");
    }

    buttonLTLQuoteFormatButtonOnClick(event: ClickEvent) {
        const rateConfFormat = this.activeRow?.get("brk_quote_template") as string;
        McLeodClassicIntegration.openClassicScreen("com.tms.client.loadmaster.drs.EntryReportTemplate",
            "<ext> search report_template.template_number='" + rateConfFormat + "' and report_template.document_type='Y'");
    }

    buttonOpenClassicCarrierQualificationOnClick(event: ClickEvent) {
        if (!StringUtil.isEmptyString(this.mainDataSource.activeRow.get("brk_qual_profile"))) {
            McLeodClassicIntegration.openClassicScreen("com.tms.client.loadmaster.brdsp.EntryBrkQualProfile",
                this.mainDataSource.activeRow.get("brk_qual_profile"));
        }
        else {
            McLeodClassicIntegration.openClassicScreen("com.tms.client.loadmaster.brdsp.EntryBrkQualProfile", "<ext> sort id");
        }
    }

    buttonOpenClassicBrokerageWorkflowOnClick(event: ClickEvent) {
        if (!StringUtil.isEmptyString(this.mainDataSource.activeRow.get("brokerage_workflow_id"))) {
            McLeodClassicIntegration.openClassicScreen("com.tms.client.loadmaster.brdsp.EntryBrokerageWorkflow",
                this.mainDataSource.activeRow.get("brokerage_workflow_id"));
        }
        else {
            McLeodClassicIntegration.openClassicScreen("com.tms.client.loadmaster.brdsp.EntryBrokerageWorkflow", "<ext> sort id");
        }
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxOrderType. */
    textboxBtmOrderTypeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_active = 'Y';
    }

    textboxDefaultScheduleBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.sched_type = 'D';
    }

    textboxRatingTriggerCodeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_active = 'Y';
    }

}
